import { Card, Button, Tag, Typography, Space, Row, Col, Statistic, Progress, Empty } from 'antd';
import {
  FileTextOutlined,
  ExperimentOutlined,
  ProfileOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  UnorderedListOutlined,
  TrophyOutlined,
} from '@ant-design/icons';
import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../api/client';
import { useAuthStore } from '../store/auth';

interface QualityTaskItem {
  id: number;
  status?: string;
}

interface CompetencyTaskItem {
  evaluateeId: number;
  status?: string;
}

interface HpTaskListItem {
  evaluatee: { id: number };
  assessment?: { isHighPotential?: boolean; status?: string } | null;
}

/** /nominator/quota 返回结构（一行 = 一个部门指派）。limit 为 null 表示该部门不限额。 */
interface NominationQuotaRow {
  deptId: number | null;
  deptName: string | null;
  headcount: number;
  limit: number | null;
  used: number;
  remaining: number | null;
}

/** /recognition-nominator/quota 返回结构（按"人"粒度聚合，与晋升提名按部门粒度不同）。 */
interface RecognitionQuotaResp {
  limit: number | null;
  used: number;
  remaining: number | null;
  deptScopes: (string | null)[];
}

interface CurrentBatchInfo {
  id: number;
  name: string;
  phase?: string;
}

/**
 * 评价人统一概览页（我的评价）
 *
 * 设计原则：仅呈现「需要评价的有什么 + 总数 / 已完成 / 未完成 / 完成率」，不展开任何被评价人明细。
 * 评价人需要点击「去打分 / 去处理」按钮跳转到对应详情页继续操作。
 *
 * 块次（按角色显示）：
 *   1) 素质项评价（360） - 全员可见，仅在被派单时有数据
 *   2) 岗位胜任力 + 高潜识别 - 仅 LEADER 可见
 *      └─ 同一个下属同时存在「胜任力」和「高潜」两个子任务，
 *         统计口径以"两者都 SUBMITTED 才算完成"，与列表页一致。
 *   3) 晋升提名 - 仅 NOMINATOR 可见
 *      └─ 总配额 = sum(部门有限 limit)；已提名 = sum(部门 used)；
 *         完成率 = 有限部门 used/limit；不限额部门单独以 footer 提示。
 */
export function MyReviewsPage() {
  const navigate = useNavigate();
  const profile = useAuthStore((s) => s.profile);
  const isLeader = profile?.roles.includes('LEADER');
  const isNominator = profile?.roles.includes('NOMINATOR');
  const isRecognitionNominator = profile?.roles.includes('RECOGNITION_NOMINATOR');
  const isAdmin = profile?.roles.includes('ADMIN') || profile?.roles.includes('SUPER_ADMIN');

  const { data: qualityTasks = [], isLoading: lq } = useQuery<QualityTaskItem[]>({
    queryKey: ['my-tasks'],
    queryFn: () => api.get('/my/eval-tasks'),
  });

  const { data: compTasks = [], isLoading: lc } = useQuery<CompetencyTaskItem[]>({
    queryKey: ['my-competency-tasks'],
    queryFn: () => api.get('/leader/competency-tasks'),
    enabled: isLeader,
  });

  const { data: hpTasks = [], isLoading: lh } = useQuery<HpTaskListItem[]>({
    queryKey: ['my-hp-tasks-overview'],
    queryFn: () => api.get('/leader/hp-tasks'),
    enabled: isLeader,
  });

  /**
   * 提名概览取的是「当前批次」（ONGOING > CALCULATING）。
   * 没有当前批次时（例如已 CLOSED），后端可能返回 null —— 这种情况下
   * 配额接口也不查，整个卡片走"暂无进行中批次"的空态即可。
   *
   * 表彰提名领导（isRecognitionNominator）也用这个当前批次拉自己的 quota，
   * 所以 enabled 条件要把 isRecognitionNominator 一起包括进来。
   */
  const { data: currentBatch } = useQuery<CurrentBatchInfo | null>({
    queryKey: ['batch-current'],
    queryFn: () =>
      api
        .get<CurrentBatchInfo>('/batches/current')
        .catch(() => null) as Promise<CurrentBatchInfo | null>,
    enabled: isNominator || isRecognitionNominator,
  });

  const nominatorBatchId = currentBatch?.id;

  /**
   * queryKey 必须与 NominatorPage 用的 ['nomination-quota', batchId] 一致，
   * 否则 NominatorPage 提交/撤回提名后调 qc.invalidateQueries({ queryKey: ['nomination-quota'] })
   * 命中不到本页缓存，回到「我的评价」时"已提名"数字会停在旧值，让用户以为新提交没生效。
   */
  const { data: quotas = [], isLoading: lqQuota } = useQuery<NominationQuotaRow[]>({
    queryKey: ['nomination-quota', nominatorBatchId],
    queryFn: () => api.get('/nominator/quota', { params: { batchId: nominatorBatchId } }),
    enabled: isNominator && !!nominatorBatchId,
  });

  /**
   * 表彰提名配额（和晋升提名是孪生兄弟，但接口/数据完全独立）。
   *
   * queryKey 必须与 RecognitionNominatorPage 用的 ['recognition-quota', batchId] 一致，
   * 否则该页提交/撤回表彰后 invalidateQueries(['recognition-quota']) 命中不到本页缓存，
   * 回「我的评价」时"已提名"数字停留在旧值——这就是 2026-05-12 农俊董事长截图反馈
   * "提交了一个人表彰但进度这里没有显示"的根因。
   *
   * 接口在用户未被指派为表彰提名领导时会 throw 403 ForbiddenException，
   * 用 .catch(() => null) 兜底，前端识别为"未指派"空态。
   */
  const { data: recogQuota, isLoading: lrQuota } = useQuery<RecognitionQuotaResp | null>({
    queryKey: ['recognition-quota', nominatorBatchId],
    queryFn: () =>
      api
        .get<RecognitionQuotaResp>('/recognition-nominator/quota', {
          params: { batchId: nominatorBatchId },
        })
        .catch(() => null) as Promise<RecognitionQuotaResp | null>,
    enabled: isRecognitionNominator && !!nominatorBatchId,
  });

  const qTotal = qualityTasks.length;
  const qDone = qualityTasks.filter((t) => t.status === 'SUBMITTED').length;
  const qPending = qTotal - qDone;
  const qPct = qTotal === 0 ? 0 : Math.round((qDone / qTotal) * 100);

  /**
   * 胜任力 + 高潜的"完成"定义：同一个下属胜任力 + 高潜两份评估都 SUBMITTED 才算 1 个完成。
   * 任意一项未提交都算未完成。这样评价人在概览里看到"还有 N 个未完成"时，
   * 跳进详情就能直接看到那些缺一份/缺两份的下属。
   */
  const hpStatusByEmpId = new Map<number, string | undefined>();
  for (const t of hpTasks) {
    hpStatusByEmpId.set(t.evaluatee.id, t.assessment?.status);
  }
  const cTotal = compTasks.length;
  const cDone = compTasks.filter(
    (t) => t.status === 'SUBMITTED' && hpStatusByEmpId.get(t.evaluateeId) === 'SUBMITTED',
  ).length;
  const cPending = cTotal - cDone;
  const cPct = cTotal === 0 ? 0 : Math.round((cDone / cTotal) * 100);

  /**
   * 提名口径：
   *   - 总配额 = 所有部门 limit 之和（仅有限额度部门）
   *   - 已提名 = 所有部门 used 之和（**包含不限额部门**——只要提交了就要计入，
   *     否则像董事长这种全部不限额的领导会看到"已提名 0"误以为提交失败。
   *     2026-05-05 农俊董事长就被这个误导，是修这条注释的源动力）
   *   - 剩余  = max(0, 有限部门 limit 之和 − 有限部门 used 之和)
   *   - 完成率 = 有限部门 used / 有限部门 limit
   *   - 不限额部门单独以 footer 提示，不参与"剩余 / 完成率"计算
   *   - 当全部部门都不限额时（allUnlimited）走专用卡片，避免出现
   *     "总配额 0 / 已提名 1 / 完成率 0%" 这种自相矛盾的展示
   */
  const finiteQuotas = quotas.filter((q) => q.limit != null);
  const nQuotaTotal = finiteQuotas.reduce((acc, q) => acc + (q.limit ?? 0), 0);
  const nUsedFinite = finiteQuotas.reduce((acc, q) => acc + (q.used ?? 0), 0);
  const nUsedAll = quotas.reduce((acc, q) => acc + (q.used ?? 0), 0);
  const nPending = Math.max(0, nQuotaTotal - nUsedFinite);
  const nPct = nQuotaTotal === 0 ? 0 : Math.round((nUsedFinite / nQuotaTotal) * 100);
  const unlimitedDeptCount = quotas.length - finiteQuotas.length;
  const unlimitedUsed = nUsedAll - nUsedFinite;
  const nominationAssigned = quotas.length > 0;
  const allUnlimited = nominationAssigned && finiteQuotas.length === 0;

  /**
   * 表彰提名统计口径：
   *   - recogQuota === null：用户未被指派（或接口 403）→ 走空态卡片
   *   - recogQuota.limit === null：被指派但不限额 → 走 RecognitionUnlimitedCard
   *   - recogQuota.limit 有值：常规进度卡（总配额 / 已提名 / 剩余 / 完成率）
   *
   * 注意：表彰提名 quota 是按"人"粒度（领导自己），不是按部门数组——
   * 跟晋升提名（按部门数组求和）数据结构不同，但 UI 上呈现一致。
   */
  const rRawLimit = recogQuota?.limit ?? null;
  const rUsed = recogQuota?.used ?? 0;
  const rRemaining =
    recogQuota?.remaining ?? (rRawLimit != null ? Math.max(rRawLimit - rUsed, 0) : null);
  const rIsAssigned = !!recogQuota;
  const rAllUnlimited = rIsAssigned && rRawLimit == null;
  const rPct =
    rRawLimit != null && rRawLimit > 0 ? Math.round((rUsed / rRawLimit) * 100) : 0;
  const rDeptScopeText = recogQuota?.deptScopes?.filter(Boolean).join(' / ') ?? '';

  return (
    <div>
      <div className="tg-page-title" style={{ marginBottom: 8 }}>
        我的评价
      </div>
      <Typography.Paragraph type="secondary" style={{ marginBottom: 20 }}>
        这里集中呈现你作为评价人需要完成的所有评价类型与进度概览；点击「去打分」进入对应的详情页查看被评价人列表并完成评价
        {isAdmin ? '（你当前是管理员，若也参与评价则会出现下列待办）' : ''}。
      </Typography.Paragraph>

      <Space direction="vertical" size={20} style={{ width: '100%' }}>
        <SummaryCard
          title="素质项评价（360）"
          icon={<FileTextOutlined style={{ color: '#722ed1' }} />}
          color="#722ed1"
          loading={lq}
          total={qTotal}
          done={qDone}
          pending={qPending}
          percent={qPct}
          actionLabel={qPending > 0 ? '去打分' : '查看 / 修改'}
          onAction={() => navigate('/my/eval-tasks')}
          emptyTip="本次未被派为素质项评价人"
        />

        {isLeader && (
          <SummaryCard
            title="岗位胜任力 + 高潜识别"
            icon={<ExperimentOutlined style={{ color: '#13c2c2' }} />}
            color="#13c2c2"
            loading={lc || lh}
            total={cTotal}
            done={cDone}
            pending={cPending}
            percent={cPct}
            actionLabel={cPending > 0 ? '去打分' : '查看 / 修改'}
            onAction={() => navigate('/leader/competency')}
            emptyTip="暂无下属胜任力 / 高潜评估任务"
            footer={
              cTotal > 0 ? (
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  说明：同一名下属需同时完成「岗位胜任力」与「高潜识别」两份评估，两份均提交才计入"已完成"。
                </Typography.Text>
              ) : null
            }
          />
        )}

        {isNominator && allUnlimited && (
          <NominationUnlimitedCard
            loading={lqQuota}
            done={nUsedAll}
            batchName={currentBatch?.name ?? '-'}
            deptCount={quotas.length}
            onAction={() => navigate('/nominator')}
          />
        )}

        {isNominator && !allUnlimited && (
          <SummaryCard
            title="晋升提名"
            titleNote="说明：根据实际情况提名，不一定追求 100% 用满提名名额"
            icon={<ProfileOutlined style={{ color: '#52c41a' }} />}
            color="#52c41a"
            loading={lqQuota}
            total={nQuotaTotal}
            // 已提名 = 所有部门 used 之和（包含不限额部门）。
            // 之前传 nUsedFinite 会让全不限额领导看到"已提名 0"以为提交失败。
            done={nUsedAll}
            pending={nPending}
            percent={nPct}
            totalLabel="总配额"
            doneLabel="已提名"
            pendingLabel="剩余"
            totalSuffix="人"
            actionLabel={nPending > 0 || unlimitedDeptCount > 0 ? '去提名' : '查看 / 修改'}
            onAction={() => navigate('/nominator')}
            emptyOverride={!nominationAssigned}
            actionDisabled={!nominationAssigned}
            emptyTip={!currentBatch ? '暂无进行中的提名批次' : '本批次暂未被指派为提名领导'}
            footer={
              nominationAssigned ? (
                <Space direction="vertical" size={4} style={{ display: 'flex' }}>
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    本期批次：
                    <Typography.Text strong style={{ fontSize: 12 }}>
                      {currentBatch?.name ?? '-'}
                    </Typography.Text>
                    {`，HR 共下发 ${quotas.length} 个部门`}
                  </Typography.Text>
                  {unlimitedDeptCount > 0 && (
                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                      其中 {unlimitedDeptCount} 个部门未配置上限（不限额，已提名 {unlimitedUsed} 人，不计入完成率）
                    </Typography.Text>
                  )}
                </Space>
              ) : null
            }
          />
        )}

        {/*
          表彰提名进度卡（2026-05-12 农俊董事长现场提）：
          - 不限额时走 RecognitionUnlimitedCard 单一数字"已提名 X 人"
          - 有限额时走 SummaryCard 四块统计（与晋升提名同结构，方便用户认识）
          queryKey 与 RecognitionNominatorPage 一致 → 表彰提名提交后自动联动刷新
        */}
        {isRecognitionNominator && rAllUnlimited && (
          <RecognitionUnlimitedCard
            loading={lrQuota}
            done={rUsed}
            batchName={currentBatch?.name ?? '-'}
            deptScopeText={rDeptScopeText}
            onAction={() => navigate('/recognition-nominator')}
          />
        )}

        {isRecognitionNominator && !rAllUnlimited && (
          <SummaryCard
            title="表彰提名"
            titleNote="说明：根据实际情况提名，不一定追求 100% 用满表彰名额"
            icon={<TrophyOutlined style={{ color: '#fa8c16' }} />}
            color="#fa8c16"
            loading={lrQuota}
            total={rRawLimit ?? 0}
            done={rUsed}
            pending={rRemaining ?? 0}
            percent={rPct}
            totalLabel="总配额"
            doneLabel="已提名"
            pendingLabel="剩余"
            totalSuffix="人"
            actionLabel={(rRemaining ?? 0) > 0 ? '去提名' : '查看 / 修改'}
            onAction={() => navigate('/recognition-nominator')}
            emptyOverride={!rIsAssigned}
            actionDisabled={!rIsAssigned}
            emptyTip={!currentBatch ? '暂无进行中的批次' : '本批次暂未被指派为表彰提名领导'}
            footer={
              rIsAssigned ? (
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  本期批次：
                  <Typography.Text strong style={{ fontSize: 12 }}>
                    {currentBatch?.name ?? '-'}
                  </Typography.Text>
                  {rDeptScopeText && `，可表彰范围：${rDeptScopeText}`}
                </Typography.Text>
              ) : null
            }
          />
        )}
      </Space>
    </div>
  );
}

/**
 * 表彰提名"不限额"场景的专用卡片，结构 = NominationUnlimitedCard 的"换皮"版。
 * 单独成函数是因为颜色/图标/文案与晋升提名有差异：橙黄 + Trophy 图标。
 */
function RecognitionUnlimitedCard({
  loading,
  done,
  batchName,
  deptScopeText,
  onAction,
}: {
  loading?: boolean;
  done: number;
  batchName: string;
  deptScopeText: string;
  onAction: () => void;
}) {
  return (
    <Card
      title={
        <Space size={12} wrap>
          <Space>
            <TrophyOutlined style={{ color: '#fa8c16' }} />
            表彰提名
          </Space>
          <Typography.Text type="secondary" style={{ fontSize: 12, fontWeight: 'normal' }}>
            说明：本期未配置表彰上限，按实际情况提名即可
          </Typography.Text>
        </Space>
      }
      loading={loading}
      style={{ borderTop: '3px solid #fa8c16' }}
      extra={
        <Button type="primary" onClick={onAction}>
          {done > 0 ? '继续提名 / 修改' : '去提名'}
        </Button>
      }
    >
      <Row align="middle" gutter={24}>
        <Col xs={24} md={10}>
          <Statistic
            title={
              <span>
                <CheckCircleOutlined style={{ color: '#fa8c16', marginRight: 6 }} />
                已提名
              </span>
            }
            value={done}
            valueStyle={{ color: '#fa8c16' }}
            suffix="人"
          />
        </Col>
        <Col xs={24} md={14}>
          <Typography.Text type="secondary" style={{ fontSize: 13, lineHeight: 1.7 }}>
            本期批次：
            <Typography.Text strong style={{ fontSize: 13 }}>
              {batchName}
            </Typography.Text>
            <br />
            {deptScopeText
              ? `可表彰范围：${deptScopeText}，本期未配置表彰上限。`
              : '本期未配置表彰上限。'}
            {done > 0 ? '已提交的表彰提名均已计入。' : '请点击右上角去提名按钮提交表彰。'}
          </Typography.Text>
        </Col>
      </Row>
    </Card>
  );
}

/**
 * 全部部门均不限额时的"晋升提名"专用卡片。
 *
 * 业务背景：董事长/集团高管这种最顶层提名领导，HR 通常不会给他们配人数上限
 * （quota=null）。原 SummaryCard 用"总配额 / 已提名 / 剩余 / 完成率"四块统计
 * 来描述提名进度，但全不限额场景下"总配额=0"会让"已提名 X"看起来像还没动；
 * 2026-05-05 农俊董事长就是被这个 UI 误导，看不见自己已提交的 1 条提名。
 *
 * 新设计：单一大数字"已提名 X 人" + 注释说明本期未配置上限，逻辑零歧义。
 */
function NominationUnlimitedCard({
  loading,
  done,
  batchName,
  deptCount,
  onAction,
}: {
  loading?: boolean;
  done: number;
  batchName: string;
  deptCount: number;
  onAction: () => void;
}) {
  return (
    <Card
      title={
        <Space size={12} wrap>
          <Space>
            <ProfileOutlined style={{ color: '#52c41a' }} />
            晋升提名
          </Space>
          <Typography.Text type="secondary" style={{ fontSize: 12, fontWeight: 'normal' }}>
            说明：本期未配置提名上限，按实际情况提名即可
          </Typography.Text>
        </Space>
      }
      loading={loading}
      style={{ borderTop: '3px solid #52c41a' }}
      extra={
        <Button type="primary" onClick={onAction}>
          {done > 0 ? '继续提名 / 修改' : '去提名'}
        </Button>
      }
    >
      <Row align="middle" gutter={24}>
        <Col xs={24} md={10}>
          <Statistic
            title={
              <span>
                <CheckCircleOutlined style={{ color: '#52c41a', marginRight: 6 }} />
                已提名
              </span>
            }
            value={done}
            valueStyle={{ color: '#52c41a' }}
            suffix="人"
          />
        </Col>
        <Col xs={24} md={14}>
          <Typography.Text type="secondary" style={{ fontSize: 13, lineHeight: 1.7 }}>
            本期批次：
            <Typography.Text strong style={{ fontSize: 13 }}>
              {batchName}
            </Typography.Text>
            <br />
            HR 已下发 {deptCount} 个部门，本期均未配置提名上限（不限额）。
            {done > 0 ? '已提交的提名均已计入。' : '请点击右上角去提名按钮提交提名。'}
          </Typography.Text>
        </Col>
      </Row>
    </Card>
  );
}

interface SummaryCardProps {
  title: string;
  icon: ReactNode;
  color: string;
  loading?: boolean;
  total: number;
  done: number;
  pending: number;
  percent: number;
  actionLabel: string;
  onAction: () => void;
  emptyTip: string;
  footer?: ReactNode;
  /** 默认 "总数"，提名场景可传 "总配额" */
  totalLabel?: string;
  /** 默认 "已完成"，提名场景可传 "已提名" */
  doneLabel?: string;
  /** 默认 "未完成"，提名场景可传 "剩余" */
  pendingLabel?: string;
  /** 数字单位，默认 "人" */
  totalSuffix?: string;
  /**
   * 是否走空态分支，默认 total === 0。
   * 提名场景可能 total=0 但仍有"不限额"任务，此时应传 false 以保留按钮可点。
   */
  emptyOverride?: boolean;
  /** 主按钮是否禁用，默认 total === 0 */
  actionDisabled?: boolean;
  /** 标题旁的灰色补充说明（如提名规则提示） */
  titleNote?: string;
}

/**
 * 单个评价类型的概览卡片：总数 / 已完成 / 未完成 / 完成率。
 * 不渲染任何被评价人 / 被提名人明细，跳转入口由调用方注入。
 *
 * 当 total=0 时显示空态（emptyTip），右上按钮 disabled。
 * 提名场景可通过 totalLabel/doneLabel/pendingLabel 改用"总配额/已提名/剩余"。
 */
function SummaryCard({
  title,
  icon,
  color,
  loading,
  total,
  done,
  pending,
  percent,
  actionLabel,
  onAction,
  emptyTip,
  footer,
  totalLabel = '总数',
  doneLabel = '已完成',
  pendingLabel = '未完成',
  totalSuffix = '人',
  emptyOverride,
  actionDisabled,
  titleNote,
}: SummaryCardProps) {
  const isEmpty = emptyOverride ?? total === 0;
  const isDisabled = actionDisabled ?? total === 0;
  return (
    <Card
      title={
        <Space size={12} wrap>
          <Space>
            {icon}
            {title}
          </Space>
          {titleNote && (
            <Typography.Text type="secondary" style={{ fontSize: 12, fontWeight: 'normal' }}>
              {titleNote}
            </Typography.Text>
          )}
        </Space>
      }
      loading={loading}
      style={{ borderTop: `3px solid ${color}` }}
      extra={
        <Button type="primary" disabled={isDisabled} onClick={onAction}>
          {actionLabel}
        </Button>
      }
    >
      {isEmpty ? (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={emptyTip}>
          {footer}
        </Empty>
      ) : (
        <>
          <Row gutter={16} align="middle">
            <Col xs={24} md={16}>
              <Row gutter={16}>
                <Col span={8}>
                  <Statistic
                    title={
                      <span>
                        <UnorderedListOutlined style={{ marginRight: 6 }} />
                        {totalLabel}
                      </span>
                    }
                    value={total}
                    suffix={totalSuffix}
                  />
                </Col>
                <Col span={8}>
                  <Statistic
                    title={
                      <span>
                        <CheckCircleOutlined style={{ color: '#52c41a', marginRight: 6 }} />
                        {doneLabel}
                      </span>
                    }
                    value={done}
                    valueStyle={{ color: '#52c41a' }}
                    suffix={totalSuffix}
                  />
                </Col>
                <Col span={8}>
                  <Statistic
                    title={
                      <span>
                        <ClockCircleOutlined
                          style={{ color: pending > 0 ? '#fa8c16' : '#999', marginRight: 6 }}
                        />
                        {pendingLabel}
                      </span>
                    }
                    value={pending}
                    valueStyle={{ color: pending > 0 ? '#fa8c16' : '#999' }}
                    suffix={totalSuffix}
                  />
                </Col>
              </Row>
            </Col>
            <Col xs={24} md={8}>
              <div style={{ paddingLeft: 12 }}>
                <div style={{ marginBottom: 4, color: '#666', fontSize: 13 }}>
                  完成率
                  {pending === 0 && total > 0 && (
                    <Tag color="success" style={{ marginLeft: 8 }}>已全部完成</Tag>
                  )}
                </div>
                <Progress
                  percent={percent}
                  strokeColor={{ '0%': '#108ee9', '100%': '#87d068' }}
                  format={(p) => `${p}%`}
                />
              </div>
            </Col>
          </Row>
          {footer && <div style={{ marginTop: 12 }}>{footer}</div>}
        </>
      )}
    </Card>
  );
}
